import React, { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, Pill, EmptyState, Skeleton } from "../../Components/ui";

function fmtMs(ms) {
  if (!ms || ms < 0) return "—";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600_000) return `${Math.round(ms / 60_000)}m`;
  if (ms < 86400_000) return `${(ms / 3600_000).toFixed(1)}h`;
  return `${(ms / 86400_000).toFixed(1)}d`;
}
function pct(n) { return `${(n || 0).toFixed(1)}%`; }
function speedColor(sf) {
  if (!sf) return "var(--text-tertiary)";
  if (sf < 0.85) return "#10B981";
  if (sf < 1.1)  return "var(--accent-primary)";
  if (sf < 1.5)  return "#F59E0B";
  return "#EF4444";
}

const TABS = [
  { id: "employees",   label: "Employees" },
  { id: "tasks",       label: "Tasks" },
  { id: "departments", label: "Departments" },
  { id: "projects",    label: "Projects" },
  { id: "bottlenecks", label: "Bottlenecks" },
];

function DelayPill({ cls }) {
  if (!cls || cls === "onTime") return <Pill tone="success" size="sm">on time</Pill>;
  if (cls === "softDelay")      return <Pill tone="warning" size="sm">soft</Pill>;
  return <Pill tone="danger" size="sm">hard</Pill>;
}

export default function AnalyticsPage() {
  const role = localStorage.getItem("role");
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState("employees");
  const [days, setDays] = useState(30);

  useShellHeader({ title: "Analytics", breadcrumb: "Operational Intelligence" }, []);

  if (role !== "SUPER_ADMIN") {
    return (
      <Card padding="lg">
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>Restricted</div>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
          Analytics dashboards are available to administrators only.
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Tab strip + range selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div
          style={{
            display: "inline-flex",
            background: "var(--bg-subtle)",
            padding: 3,
            borderRadius: 10,
            gap: 2,
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  padding: "5px 12px",
                  fontSize: 13,
                  fontWeight: 500,
                  borderRadius: 7,
                  background: active ? "var(--bg-surface)" : "transparent",
                  color: active ? "var(--text-primary)" : "var(--text-secondary)",
                  boxShadow: active ? "var(--shadow-xs)" : "none",
                  cursor: "pointer",
                  transition: "all 160ms cubic-bezier(0.4,0,0.2,1)",
                  border: "none",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          style={{
            fontSize: 13,
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            padding: "6px 10px",
            background: "var(--bg-surface)",
            color: "var(--text-primary)",
          }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {tab === "employees"   && <EmployeesPanel days={days} navigate={navigate} />}
      {tab === "tasks"       && <TasksPanel navigate={navigate} />}
      {tab === "departments" && <DeptsPanel days={days} />}
      {tab === "projects"    && <ProjectsPanel />}
      {tab === "bottlenecks" && <BottlenecksPanel navigate={navigate} />}

      <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 12 }}>
        Speed factor &lt;1.0 = faster than expected. Computed from task complexity, priority, department.
      </div>
    </div>
  );
}

// ----- Shared table primitives -----
const tableStyle = { width: "100%", fontSize: 13, borderCollapse: "collapse" };
const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-tertiary)",
  background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const thRight = { ...thStyle, textAlign: "right" };
const tdBase = { padding: "10px 12px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-secondary)" };
const tdRight = { ...tdBase, textAlign: "right" };

function rowHover(e) { e.currentTarget.style.background = "var(--bg-subtle)"; }
function rowReset(e) { e.currentTarget.style.background = "transparent"; }

function TableLoading() {
  return (
    <div style={{ padding: 16 }}>
      <Skeleton h={12} w="40%" />
      <Skeleton h={28} w="100%" style={{ marginTop: 12 }} />
      <Skeleton h={28} w="100%" style={{ marginTop: 8 }} />
      <Skeleton h={28} w="100%" style={{ marginTop: 8 }} />
    </div>
  );
}

// ----- Employees -----
function EmployeesPanel({ days, navigate }) {
  const [data, setData] = useState({ employees: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api(`/analytics/employees?days=${days}`)
      .then((d) => setData(d))
      .catch(() => setData({ employees: [] }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <Card padding="none">
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Employee leaderboard</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>Sorted by speed factor (faster = lower)</div>
      </div>
      {loading ? <TableLoading /> : data.employees.length === 0 ? (
        <EmptyState icon="📊" title="No completed tasks in this window" />
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Employee</th>
              <th style={thRight}>Done</th>
              <th style={thRight}>Avg work</th>
              <th style={thRight}>Avg idle</th>
              <th style={thRight}>Speed</th>
              <th style={thRight}>Delay %</th>
              <th style={thRight}>Reopens</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map((e) => (
              <tr key={e.user._id} onMouseEnter={rowHover} onMouseLeave={rowReset} style={{ transition: "background 120ms" }}>
                <td style={tdBase}>
                  <RouterLink to={`/users/${e.user._id}`} style={{ textDecoration: "none" }}>
                    <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{e.user.username}</div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{e.user.designation || e.user.email}</div>
                  </RouterLink>
                </td>
                <td className="tabular" style={{ ...tdRight, color: "var(--text-primary)", fontWeight: 500 }}>{e.tasksCompleted}</td>
                <td className="tabular" style={tdRight}>{fmtMs(e.avgWorkMs)}</td>
                <td className="tabular" style={tdRight}>{fmtMs(e.avgIdleMs)}</td>
                <td className="tabular" style={{ ...tdRight, color: speedColor(e.avgSpeedFactor), fontWeight: 600 }}>{e.avgSpeedFactor.toFixed(2)}</td>
                <td className="tabular" style={tdRight}>{pct(e.delayPct)}</td>
                <td className="tabular" style={tdRight}>{e.reopens || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ----- Tasks -----
function TasksPanel({ navigate }) {
  const [filter, setFilter] = useState("longest");
  const [data, setData] = useState({ tasks: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api(`/analytics/tasks?filter=${filter}&limit=30`)
      .then((d) => setData(d))
      .catch(() => setData({ tasks: [] }))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <Card padding="none">
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Task analytics</div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{
          fontSize: 12, border: "1px solid var(--border-default)", borderRadius: 6,
          padding: "4px 8px", background: "var(--bg-surface)", color: "var(--text-primary)",
        }}>
          <option value="longest">Longest running</option>
          <option value="delayed">Most delayed</option>
          <option value="reopened">Most revised</option>
          <option value="stuck">Stuck in review</option>
        </select>
      </div>
      {loading ? <TableLoading /> : data.tasks.length === 0 ? (
        <EmptyState icon="✨" title="No matching tasks" />
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Task</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Project</th>
              <th style={thRight}>Total</th>
              <th style={thRight}>Speed</th>
              <th style={thRight}>Reopens</th>
              <th style={thRight}>Delay</th>
            </tr>
          </thead>
          <tbody>
            {data.tasks.map((t) => (
              <tr key={t._id} onMouseEnter={rowHover} onMouseLeave={rowReset} style={{ transition: "background 120ms" }}>
                <td style={tdBase}>
                  <button onClick={() => navigate(`/analytics/timeline/task/${t._id}`)} style={{ textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
                    <div style={{ fontWeight: 500, color: "var(--text-primary)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>{t.priority || "normal"} · c{t.complexity || 3} · {t.department || "—"}</div>
                  </button>
                </td>
                <td style={{ ...tdBase, fontSize: 11 }}>{t.status}</td>
                <td style={{ ...tdBase, fontSize: 11 }}>{t.projectId?.name || "—"}</td>
                <td className="tabular" style={{ ...tdRight, color: "var(--text-primary)", fontWeight: 500 }}>{fmtMs(t.metrics?.totalCompletionMs)}</td>
                <td className="tabular" style={{ ...tdRight, color: speedColor(t.metrics?.speedFactor), fontWeight: 600 }}>{(t.metrics?.speedFactor || 0).toFixed(2)}</td>
                <td className="tabular" style={tdRight}>{t.reopenCount || 0}</td>
                <td style={tdRight}><DelayPill cls={t.metrics?.delayClass} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ----- Departments -----
function MetricCell({ label, value, valueColor = "var(--text-primary)" }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{label}</div>
      <div className="tabular" style={{ fontSize: 14, fontWeight: 600, color: valueColor, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function DeptsPanel({ days }) {
  const [data, setData] = useState({ departments: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api(`/analytics/departments?days=${days}`)
      .then((d) => setData(d))
      .catch(() => setData({ departments: [] }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <Card padding="none">
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
        Department analytics
      </div>
      {loading ? <TableLoading /> : data.departments.length === 0 ? (
        <EmptyState icon="🏢" title="No teams found" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, padding: 12 }}>
          {data.departments.map((d) => (
            <Card key={d.team._id} padding="md" variant="subtle">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{d.team.name}</div>
                <DelayPill cls={d.delayPct > 30 ? "hardDelay" : d.delayPct > 10 ? "softDelay" : "onTime"} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <MetricCell label="Completed" value={d.tasksCompleted} />
                <MetricCell label="Avg work"  value={fmtMs(d.avgWorkMs)} />
                <MetricCell label="Avg idle"  value={fmtMs(d.avgIdleMs)} />
                <MetricCell label="Avg review" value={fmtMs(d.avgReviewDelayMs)} />
                <MetricCell label="Speed"     value={(d.avgSpeedFactor || 0).toFixed(2)} valueColor={speedColor(d.avgSpeedFactor)} />
                <MetricCell label="On time"   value={d.onTime} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
}

// ----- Projects -----
function ProjectsPanel() {
  const [data, setData] = useState({ projects: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api(`/analytics/projects`)
      .then((d) => setData(d))
      .catch(() => setData({ projects: [] }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card padding="none">
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
        Project health
      </div>
      {loading ? <TableLoading /> : data.projects.length === 0 ? (
        <EmptyState icon="📁" title="No projects found" />
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Project</th>
              <th style={thRight}>Completed</th>
              <th style={thRight}>Avg work</th>
              <th style={thRight}>Speed</th>
              <th style={thRight}>On time</th>
              <th style={thRight}>Hard delays</th>
            </tr>
          </thead>
          <tbody>
            {data.projects.map((p) => (
              <tr key={p.project._id} onMouseEnter={rowHover} onMouseLeave={rowReset} style={{ transition: "background 120ms" }}>
                <td style={tdBase}>
                  <RouterLink to={`/projects/${p.project._id}`} style={{ textDecoration: "none", color: "var(--text-primary)", fontWeight: 500 }}>
                    <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 9999, background: p.project.color || "#999", marginRight: 8, verticalAlign: "middle" }} />
                    {p.project.name}
                  </RouterLink>
                </td>
                <td className="tabular" style={tdRight}>{p.tasksCompleted}</td>
                <td className="tabular" style={tdRight}>{fmtMs(p.avgWorkMs)}</td>
                <td className="tabular" style={{ ...tdRight, color: speedColor(p.avgSpeedFactor), fontWeight: 600 }}>{(p.avgSpeedFactor || 0).toFixed(2)}</td>
                <td className="tabular" style={{ ...tdRight, color: "#10B981" }}>{p.onTime}</td>
                <td className="tabular" style={{ ...tdRight, color: "#EF4444" }}>{p.hardDelay}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ----- Bottlenecks -----
function BottlenecksPanel({ navigate }) {
  const [data, setData] = useState({ stuckInReview: [], overdueActive: [], overloaded: [], underloaded: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api(`/analytics/bottlenecks`)
      .then((d) => setData({ ...d, underloaded: d.underloaded || [] }))
      .catch(() => setData({ stuckInReview: [], overdueActive: [], overloaded: [], underloaded: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <TableLoading />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card padding="none">
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Stuck in review</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-tertiary)" }}>&gt;2 days · {data.stuckInReview.length}</span>
        </div>
        {data.stuckInReview.length === 0 ? (
          <EmptyState icon="✨" title="No stuck reviews" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {data.stuckInReview.map((t, i) => (
              <li key={t._id} style={{ padding: "10px 16px", borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)", transition: "background 120ms" }}
                  onMouseEnter={rowHover} onMouseLeave={rowReset}>
                <button onClick={() => navigate(`/analytics/timeline/task/${t._id}`)} style={{ display: "flex", width: "100%", textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", gap: 12, alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                      {t.projectId?.name || "—"} · {(t.assignees || []).map((a) => a.username).join(", ") || "unassigned"}
                    </div>
                  </div>
                  <Pill tone="warning" size="sm">{fmtMs(Date.now() - new Date(t.submittedAt).getTime())} ago</Pill>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="none">
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Overdue active tasks</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-tertiary)" }}>{data.overdueActive.length}</span>
        </div>
        {data.overdueActive.length === 0 ? (
          <EmptyState icon="🎯" title="No overdue tasks" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {data.overdueActive.map((t, i) => (
              <li key={t._id} style={{ padding: "10px 16px", borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 12, transition: "background 120ms" }}
                  onMouseEnter={rowHover} onMouseLeave={rowReset}>
                <button onClick={() => navigate(`/analytics/timeline/task/${t._id}`)} style={{ flex: 1, minWidth: 0, textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                    {t.projectId?.name || "—"} · {t.status} · {(t.assignees || []).map((a) => a.username).join(", ") || "unassigned"}
                  </div>
                </button>
                <Pill tone="danger" size="sm">due {fmtMs(Date.now() - new Date(t.dueDate).getTime())} ago</Pill>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="none">
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Overloaded employees</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-tertiary)" }}>&gt;10 active · {data.overloaded.length}</span>
        </div>
        {data.overloaded.length === 0 ? (
          <EmptyState icon="👍" title="No overloaded employees" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {data.overloaded.map((o, i) => (
              <li key={o.user._id} style={{ padding: "10px 16px", borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <RouterLink to={`/users/${o.user._id}`} style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", textDecoration: "none" }}>
                  {o.user.username || o.user.email}
                </RouterLink>
                <Pill tone="warning">{o.active} active</Pill>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="none">
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Underloaded employees</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-tertiary)" }}>no active tasks · {data.underloaded.length}</span>
        </div>
        {data.underloaded.length === 0 ? (
          <EmptyState icon="💪" title="Everyone has work" description="No idle employees right now." />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {data.underloaded.map((o, i) => {
              const idleHint = o.daysSinceLastCompletion === null
                ? "never completed a task"
                : o.daysSinceLastCompletion === 0
                  ? "finished work today"
                  : `${o.daysSinceLastCompletion}d since last completion`;
              const tone = o.daysSinceLastCompletion === null || o.daysSinceLastCompletion > 7 ? "danger"
                : o.daysSinceLastCompletion > 3 ? "warning"
                : "neutral";
              return (
                <li key={o.user._id} style={{ padding: "10px 16px", borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <RouterLink to={`/users/${o.user._id}`} style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", textDecoration: "none" }}>
                      {o.user.username || o.user.email}
                    </RouterLink>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
                      {o.user.designation || "—"}{o.user.team?.name ? ` · ${o.user.team.name}` : ""}
                    </div>
                  </div>
                  <Pill tone={tone} size="sm">{idleHint}</Pill>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
